import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Tooltip,
  Typography,
  Icons,
  getColor,
  Checkbox,
  Button,
  useToast,
} from "fds";
import utilsDocs from "../../shared/utils/docs";
import WorkgroupAvatar from "../../components/WorkgroupAvatar";
import useCheckboxGroup from "../../shared/hooks/useCheckboxGroup";
import { t } from "i18next";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import { Workgroup } from "../../types/workgroup";
import { nanoid } from "@reduxjs/toolkit";
import { UploadFile } from "../../types/files";
import { Trans } from "react-i18next";

interface SelectDocShareChildrenProps {
  open: boolean;
  currentWorkgroupId?: string;
  onClose: () => void;
}
const SelectDocShareChildren: React.FC<SelectDocShareChildrenProps> = ({
  open,
  currentWorkgroupId,
  onClose,
}) => {
  const toast = useToast();
  const selectWorkgroupList = useCheckboxGroup<string>();
  const {
    documentState,
    removeFileFromUid,
    setUpdateUploadProgressFiles,
    doDocUploadFileComplete,
  } = useDocumentStore();
  const { workgroupState } = useWorkgroupStore();

  /** State 정리 */
  const [uid, changeUid] = useState("");
  const [files, changeFiles] = useState([]);
  const [subWorkgroupList, changeSubWorkgroupList] = useState<Workgroup[]>([]); // 하위 워크그룹 리스트

  /** State 정리 */
  useEffect(() => {
    changeSubWorkgroupList(
      workgroupState?.workgroupSidemenuList?.filter(
        (item: Workgroup) => item.parentWorkgroupId === currentWorkgroupId
      )
    );
  }, [workgroupState?.workgroupNestedSidemenuList]);
  useEffect(() => {
    changeFiles(documentState.uploadDocsProgress?.uploadFiles);
  }, [documentState.uploadDocsProgress?.uploadFiles]);
  useEffect(() => {
    changeUid(documentState.uid);
  }, [documentState.uid]);

  /** 파일 최종 업로드 */
  const handleUploadDocumentComplete = async () => {
    let completeResp: any = await doDocUploadFileComplete(uid, {
      workgroupId: currentWorkgroupId ?? "",
      permissionList: [{ changeCode: "CREATE", targetType: "WORKGROUP" }],
      folderId: "",
      targetWorkgroupIdList: selectWorkgroupList.selected,
      isMultiple: true,
    });
    if (completeResp?.payload?.error || completeResp?.error) {
      toast.toastMsg(
        nanoid(),
        completeResp?.payload?.error?.message ??
          completeResp?.error?.message ??
          t("common.alert.internalError"),
        "error"
      );
      setUpdateUploadProgressFiles({} as UploadFile);
      onClose();
    } else {
      toast.toastMsg(nanoid(), t("file.progress.shareInfo"), "success");
      setUpdateUploadProgressFiles({} as UploadFile);
      onClose();
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("file.selectWorkgroupToShare")}
      width={540}
      footer={
        <Box alignItems="center" width={"100%"}>
          <Button
            label={
              selectWorkgroupList.selected.length ===
                subWorkgroupList?.length && subWorkgroupList.length > 0
                ? t("common.unselectAll")
                : t("common.selectAll")
            }
            state={subWorkgroupList.length === 0 ? "noClick" : "default"}
            textColor={
              subWorkgroupList.length === 0
                ? getColor("COLOR_TEXT_INTERACTIVE_BRAND_DISABLED")
                : getColor("COLOR_TEXT_BRAND")
            }
            onClick={() =>
              selectWorkgroupList.handleAllClick(
                selectWorkgroupList.selected.length === subWorkgroupList?.length
                  ? []
                  : subWorkgroupList?.map((item) => item.workgroupId)
              )
            }
            btnStyle="link"
            classes="mg mr-auto hover-color--green"
          />
          <Box alignItems="center" classes="mg ml-auto">
            <Button
              label={t("common.cancel")}
              btnStyle="fill"
              type="secondary"
              onClick={onClose}
            />
            <Button
              label={t("file.share")}
              btnStyle="fill"
              classes="mg ml-8"
              onClick={handleUploadDocumentComplete}
              state={
                selectWorkgroupList.selected.length === 0
                  ? "disable"
                  : "default"
              }
            />
          </Box>
        </Box>
      }
      body={
        <Box direction="column">
          <Box classes="upload-fileList" style={{ marginTop: 0 }}>
            {files.map((file: any) => (
              <Box classes="upload-file">
                <Box>
                  {utilsDocs.renderDocIcon(
                    file.fileName ?? file.contentName,
                    20
                  )}
                </Box>
                <Tooltip
                  message={file.fileName ?? file.contentName}
                  maxWidth={400}
                >
                  <Typography type="body4_rg" ellipsis classes="mg ml-4">
                    {file.fileName ?? file.contentName}
                  </Typography>
                </Tooltip>
                {files.length > 1 && (
                  <Box
                    alignItems="center"
                    classes="mg ml-4 cursor pointer"
                    onClick={() => {
                      uid && removeFileFromUid(uid, file.uuid, file.fileId);
                      changeFiles(
                        files.filter((item: any) => item.fileId !== file.fileId)
                      );
                    }}
                  >
                    <Icons variant="line" label="closeLarge" size={12} />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
          <Box alignItems="center" classes="mg mt-8">
            <Typography type="subtitle2">
              <Trans
                i18nKey="workgroup.subWorkgroups"
                values={{ count: subWorkgroupList?.length ?? 0 }}
                components={{
                  1: (
                    <Typography
                      type="subtitle2"
                      exactColor={getColor("COLOR_TEXT_BRAND")}
                    />
                  ),
                }}
              />
            </Typography>
          </Box>
          <Box
            height="418px"
            classes="hover-scroll mg mt-12"
            direction="column"
          >
            {subWorkgroupList?.map((item: Workgroup) => (
              <Box
                minHeight={48}
                alignItems="center"
                style={{ padding: "8px", boxSizing: "border-box" }}
              >
                <WorkgroupAvatar item={item} size="32" />
                <Tooltip
                  message={item.workgroupName}
                  classes="mg ml-4"
                  maxWidth={"calc(100% - 70px)"}
                >
                  <Typography type="body2_rg" ellipsis>
                    {item.workgroupName}
                  </Typography>
                </Tooltip>
                <Checkbox
                  roundshape
                  classDiv="mg ml-auto"
                  onClick={() =>
                    selectWorkgroupList.handleClick(item.workgroupId)
                  }
                  check={selectWorkgroupList.isSelected(item.workgroupId)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      }
    />
  );
};
export default SelectDocShareChildren;
